import { motion } from 'motion/react';
import { useScrollAnimation } from '../hooks/useScrollAnimation';
import { useState, useRef } from 'react';
import { Scale, FileText, Building2, Users, Briefcase, Globe, TrendingUp, Factory, Landmark, ShoppingBag, Plane, Fuel, ArrowRight, ChevronLeft, ChevronRight, Shield, Gavel, BookOpen, Heart, Cpu, HardHat } from 'lucide-react';

export const CorePracticeAreasSection = () => {
  const { ref, isVisible } = useScrollAnimation(0.1);
  const [activeTab, setActiveTab] = useState<'practices' | 'sectors'>('practices');
  const [showAllPractices, setShowAllPractices] = useState(false);
  const [showAllSectors, setShowAllSectors] = useState(false);
  const practicesScrollRef = useRef<HTMLDivElement>(null);
  const sectorsScrollRef = useRef<HTMLDivElement>(null);
  const [showPracticesLeftArrow, setShowPracticesLeftArrow] = useState(false);
  const [showPracticesRightArrow, setShowPracticesRightArrow] = useState(true);
  const [showSectorsLeftArrow, setShowSectorsLeftArrow] = useState(false);
  const [showSectorsRightArrow, setShowSectorsRightArrow] = useState(true);

  const practices = [
    {
      icon: Scale,
      title: 'Corporate & Commercial Law',
      description: 'Transaction structuring, governance frameworks, and commercial agreements.'
    },
    {
      icon: FileText,
      title: 'Regulatory Compliance',
      description: 'Saudi regulatory requirements, licensing, and compliance obligations.'
    },
    {
      icon: Building2,
      title: 'Foreign Investment',
      description: 'Market entry strategies, MISA licensing, and joint venture structures.'
    },
    {
      icon: Users,
      title: 'Mergers & Acquisitions',
      description: 'Transaction advisory, due diligence, and regulatory approvals.'
    },
    {
      icon: Briefcase,
      title: 'Employment & Labor Law',
      description: 'Employment contracts, Saudization compliance, and dispute resolution.'
    },
    {
      icon: Globe,
      title: 'Cross-Border Transactions',
      description: 'International agreements and legal coordination.' // removed "trade regulations"
    },
    {
      icon: Shield,
      title: 'Intellectual Property',
      description: 'Trademark registration and IP protection strategies.' // removed patent & enforcement
    },
    {
      icon: Gavel,
      title: 'Dispute Resolution',
      description: 'Commercial litigation, arbitration, and alternative dispute resolution.'
    },
    {
      icon: BookOpen,
      title: 'Contract Management',
      description: 'Drafting, negotiation, and risk assessment for complex agreements.'
    },
  ];

  const sectors = [
    // Removed Financial Services and Government & Public Sector
    { icon: Factory, name: 'Manufacturing & Industrial', description: 'Industrial operations and production facilities' },
    { icon: Building2, name: 'Real Estate', description: 'Property transactions, development projects, and real estate investments.' }, // now separate
    { icon: HardHat, name: 'Construction & Contracting', description: 'Construction contracts, project management, and contractor agreements.' }, // new entry
    { icon: Fuel, name: 'Energy & Utilities', description: 'Oil and gas, renewable energy, and utilities' },
    { icon: ShoppingBag, name: 'Retail & Consumer Goods', description: 'Distribution, franchising, and compliance' },
    { icon: Plane, name: 'Transportation & Logistics', description: 'Freight, aviation, maritime, and logistics' },
    { icon: Briefcase, name: 'Professional Services', description: 'Consulting, technology, and business services' },
    { icon: Heart, name: 'Healthcare & Life Sciences', description: 'Medical facilities, pharmaceutical, and healthcare compliance' },
    { icon: Cpu, name: 'Technology & Innovation', description: 'Tech startups, software licensing, and digital transformation' },
  ];

  // Note: HardHat icon is not in the original import list; we need to add it.
  // If you prefer to use an existing icon, replace HardHat with e.g. Factory or Building2.
  // For completeness, I'll add HardHat to the import from lucide-react.

  const handleScrollLeft = (ref: React.RefObject<HTMLDivElement | null>) => {
    if (ref.current) {
      ref.current.scrollLeft -= 300;
    }
  };

  const handleScrollRight = (ref: React.RefObject<HTMLDivElement | null>) => {
    if (ref.current) {
      ref.current.scrollLeft += 300;
    }
  };

  const handlePracticesScroll = (event: React.UIEvent<HTMLDivElement>) => {
    const currentRef = practicesScrollRef.current;
    if (currentRef) {
      setShowPracticesLeftArrow(currentRef.scrollLeft > 0);
      setShowPracticesRightArrow(currentRef.scrollLeft < currentRef.scrollWidth - currentRef.clientWidth);
    }
  };

  const handleSectorsScroll = (event: React.UIEvent<HTMLDivElement>) => {
    const currentRef = sectorsScrollRef.current;
    if (currentRef) {
      setShowSectorsLeftArrow(currentRef.scrollLeft > 0);
      setShowSectorsRightArrow(currentRef.scrollLeft < currentRef.scrollWidth - currentRef.clientWidth);
    }
  };

  return (
    <section id="our-expertise" aria-label="Core Practice Areas and Industry Sectors" className="relative bg-[#faf9f7] py-12 sm:py-16 lg:py-24 overflow-hidden w-full">
      {/* Subtle background pattern */}
      <div className="absolute inset-0 opacity-[0.25]">
        <div className="absolute inset-0" style={{
          backgroundImage: `radial-gradient(circle at 2px 2px, rgba(195, 165, 101, 1) 1px, transparent 0)`,
          backgroundSize: '48px 48px',
        }}></div>
      </div>

      {/* Gold accent gradient */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[1000px] h-[300px] bg-[#c3a565]/8 blur-[100px] rounded-full"></div>

      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 relative z-10 w-full" ref={ref}>
        {/* Section Header */}
        <div className="mb-10 sm:mb-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={isVisible ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
            className="flex items-center justify-center gap-4 mb-5"
          >
            <div className="h-[1px] w-12 sm:w-16 bg-[#c3a565]"></div>
            <span className="text-xs sm:text-sm tracking-[0.25em] sm:tracking-[0.3em] uppercase text-[#c3a565]" style={{ fontWeight: 700 }}>
              Our Expertise
            </span>
            <div className="h-[1px] w-12 sm:w-16 bg-[#c3a565]"></div>
          </motion.div>
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            animate={isVisible ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-[#2b2b2a] text-2xl sm:text-3xl lg:text-5xl mb-3"
            style={{ fontWeight: 600 }}
          >
            Practice Areas
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={isVisible ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="text-[#2b2b2a]/60 max-w-2xl mx-auto text-base sm:text-lg"
            style={{ fontWeight: 300 }}
          >
            Comprehensive legal solutions across all major business sectors
          </motion.p>
        </div>

        {/* Tab Navigation */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isVisible ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="flex justify-center gap-8 sm:gap-12 mb-10 sm:mb-12 border-b-2 border-[#c3a565]/25"
        >
          <button
            onClick={() => setActiveTab('practices')}
            className={`pb-4 transition-all duration-300 relative cursor-pointer ${
              activeTab === 'practices' 
                ? 'text-[#c3a565]' 
                : 'text-[#2b2b2a]/50 hover:text-[#2b2b2a]/70'
            }`}
            style={{ fontWeight: 600, fontSize: '1.35rem' }}
          >
            Practices
            {activeTab === 'practices' && (
              <motion.div
                layoutId="activeTab"
                className="absolute bottom-0 left-0 right-0 h-[3px] bg-[#c3a565] shadow-[0_0_10px_rgba(195,165,101,0.5)]"
                transition={{ type: "spring", stiffness: 380, damping: 30 }}
              />
            )}
          </button>
          <button
            onClick={() => setActiveTab('sectors')}
            className={`pb-4 transition-all duration-300 relative cursor-pointer ${
              activeTab === 'sectors' 
                ? 'text-[#c3a565]' 
                : 'text-[#2b2b2a]/50 hover:text-[#2b2b2a]/70'
            }`}
            style={{ fontWeight: 600, fontSize: '1.35rem' }}
          >
            Sectors
            {activeTab === 'sectors' && (
              <motion.div
                layoutId="activeTab"
                className="absolute bottom-0 left-0 right-0 h-[3px] bg-[#c3a565] shadow-[0_0_10px_rgba(195,165,101,0.5)]"
                transition={{ type: "spring", stiffness: 380, damping: 30 }}
              />
            )}
          </button>
        </motion.div>

        {/* Content Area */}
        <div>
          {activeTab === 'practices' && (
            <motion.div
              key="practices"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.5 }}
              className="relative"
            >
              {/* MOBILE: Vertical Grid Layout */}
              <div className="md:hidden grid grid-cols-1 gap-5 px-4">
                {practices.slice(0, showAllPractices ? practices.length : 5).map((practice, index) => {
                  const Icon = practice.icon;
                  return (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.5, delay: index * 0.05 }}
                      className="group"
                    >
                      <div className="bg-white border-2 border-[#c3a565]/40 rounded-2xl p-6 active:scale-[0.98] transition-all duration-300 shadow-[0_4px_20px_rgba(43,43,42,0.12)]">
                        <div className="mb-4">
                          <Icon className="w-11 h-11 text-[#c3a565]" strokeWidth={1.5} />
                        </div>
                        <span className="text-[#2b2b2a] mb-3" style={{ fontWeight: 600, fontSize: '1.35rem', lineHeight: '1.3' }}>
                          {practice.title}
                        </span>
                        <p className="text-[#2b2b2a] leading-relaxed mb-5" style={{ fontWeight: 400, fontSize: '1rem', opacity: 0.85 }}>
                          {practice.description}
                        </p>
                        {/* <button className="inline-flex items-center gap-2 text-[#c3a565] active:text-[#d4b676] transition-colors duration-200 min-h-[48px]" style={{ fontWeight: 600, fontSize: '0.95rem' }}>
                          <span>Learn More</span>
                          <ArrowRight className="w-5 h-5" strokeWidth={2.5} />
                        </button> */}
                      </div>
                    </motion.div>
                  );
                })}
                {practices.length > 5 && (
                  <motion.button
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    onClick={() => setShowAllPractices(!showAllPractices)}
                    className="w-full py-4 px-6 border-2 border-[#c3a565]/40 rounded-2xl text-[#c3a565] hover:bg-[#c3a565]/5 active:bg-[#c3a565]/10 transition-all duration-300 flex items-center justify-center gap-2 min-h-[56px]"
                    style={{ fontWeight: 600, fontSize: '1rem' }}
                  >
                    {showAllPractices ? (
                      <>
                        <span>Show Less</span>
                        <ChevronLeft className="w-5 h-5 rotate-90" strokeWidth={2.5} />
                      </>
                    ) : (
                      <>
                        <span>View All {practices.length} Practices</span>
                        <ChevronRight className="w-5 h-5 rotate-90" strokeWidth={2.5} />
                      </>
                    )}
                  </motion.button>
                )}
              </div>

              {/* DESKTOP: Horizontal Scroll Container */}
              <div className="hidden md:block relative px-12">
                <div 
                  className="grid grid-rows-2 gap-4 overflow-x-auto pb-4 scrollbar-thin scrollbar-thumb-[#c3a565]/30 scrollbar-track-transparent hover:scrollbar-thumb-[#c3a565]/50"
                  style={{
                    gridAutoFlow: 'column',
                    gridAutoColumns: 'minmax(280px, 340px)',
                    scrollBehavior: 'smooth'
                  }}
                  ref={practicesScrollRef}
                  onScroll={handlePracticesScroll}
                >
                  {practices.map((practice, index) => {
                    const Icon = practice.icon;
                    return (
                      <motion.div
                        key={index}
                        initial={{ opacity: 0, x: 30 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.6, delay: index * 0.1 }}
                        className="group"
                      >
                        <div className="bg-white border-2 border-[#c3a565]/30 rounded-xl p-6 hover:border-[#c3a565]/70 hover:shadow-[0_8px_40px_rgba(195,165,101,0.25)] hover:scale-[1.03] hover:bg-[#faf9f7] transition-all duration-500 h-full flex flex-col shadow-[0_2px_15px_rgba(43,43,42,0.08)]">
                          <div className="mb-4 group-hover:scale-110 transition-transform duration-500">
                            <Icon className="w-9 h-9 text-[#c3a565] group-hover:text-[#d4b676] transition-colors duration-500" strokeWidth={1.5} />
                          </div>
                          <span className="text-[#2b2b2a] mb-3 group-hover:text-[#c3a565] transition-colors duration-500" style={{ fontWeight: 600, fontSize: '1.15rem', lineHeight: '1.3' }}>
                            {practice.title}
                          </span>
                          <p className="text-[#2b2b2a] leading-relaxed mb-4 flex-grow group-hover:text-[#2b2b2a] transition-colors duration-500" style={{ fontWeight: 400, fontSize: '0.9rem', opacity: 0.85 }}>
                            {practice.description}
                          </p>
                          {/* <button className="inline-flex items-center gap-2 text-[#c3a565] hover:text-[#d4b676] transition-colors duration-300 group/btn" style={{ fontWeight: 600, fontSize: '0.875rem' }}>
                            <span>Learn More</span>
                            <ArrowRight className="w-4 h-4 group-hover/btn:translate-x-1 transition-transform duration-300" strokeWidth={2.5} />
                          </button> */}
                        </div>
                      </motion.div>
                    );
                  })}
                </div>
                <div className="absolute right-12 top-0 bottom-4 w-16 bg-gradient-to-l from-[#faf9f7] to-transparent pointer-events-none"></div>
                <button
                  onClick={() => handleScrollLeft(practicesScrollRef)}
                  className={`absolute -left-1 top-1/2 -translate-y-1/2 z-10 w-10 h-10 flex items-center justify-center bg-white border border-[#c3a565]/30 rounded-full hover:bg-[#c3a565]/10 hover:border-[#c3a565]/60 transition-all duration-300 shadow-lg cursor-pointer slider-arrow ${showPracticesLeftArrow ? 'visible' : 'invisible'}`}
                  aria-label="Scroll left"
                >
                  <ChevronLeft className="w-5 h-5 text-[#c3a565]" strokeWidth={2} />
                </button>
                <button
                  onClick={() => handleScrollRight(practicesScrollRef)}
                  className={`absolute -right-1 top-1/2 -translate-y-1/2 z-10 w-10 h-10 flex items-center justify-center bg-white border border-[#c3a565]/30 rounded-full hover:bg-[#c3a565]/10 hover:border-[#c3a565]/60 transition-all duration-300 shadow-lg cursor-pointer ${showPracticesRightArrow ? 'visible' : 'invisible'}`}
                  aria-label="Scroll right"
                >
                  <ChevronRight className="w-5 h-5 text-[#c3a565]" strokeWidth={2} />
                </button>
              </div>
            </motion.div>
          )}

          {activeTab === 'sectors' && (
            <motion.div
              key="sectors"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.5 }}
              className="relative"
            >
              {/* MOBILE: Vertical Grid Layout */}
              <div className="md:hidden grid grid-cols-1 gap-5 px-4">
                {sectors.slice(0, showAllSectors ? sectors.length : 5).map((sector, index) => {
                  const Icon = sector.icon;
                  return (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.5, delay: index * 0.05 }}
                      className="group"
                    >
                      <div className="bg-white border-2 border-[#c3a565]/40 rounded-2xl p-6 active:scale-[0.98] transition-all duration-300 shadow-[0_4px_20px_rgba(43,43,42,0.12)]">
                        <div className="mb-4">
                          <Icon className="w-11 h-11 text-[#c3a565]" strokeWidth={1.5} />
                        </div>
                        <span className="text-[#2b2b2a] mb-3" style={{ fontWeight: 600, fontSize: '1.35rem', lineHeight: '1.3' }}>
                          {sector.name}
                        </span>
                        <p className="text-[#2b2b2a] leading-relaxed mb-5" style={{ fontWeight: 400, fontSize: '1rem', opacity: 0.85 }}>
                          {sector.description}
                        </p>
                        {/* <button className="inline-flex items-center gap-2 text-[#c3a565] active:text-[#d4b676] transition-colors duration-200 min-h-[48px]" style={{ fontWeight: 600, fontSize: '0.95rem' }}>
                          <span>Learn More</span>
                          <ArrowRight className="w-5 h-5" strokeWidth={2.5} />
                        </button> */}
                      </div>
                    </motion.div>
                  );
                })}
                {sectors.length > 5 && (
                  <motion.button
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    onClick={() => setShowAllSectors(!showAllSectors)}
                    className="w-full py-4 px-6 border-2 border-[#c3a565]/40 rounded-2xl text-[#c3a565] hover:bg-[#c3a565]/5 active:bg-[#c3a565]/10 transition-all duration-300 flex items-center justify-center gap-2 min-h-[56px]"
                    style={{ fontWeight: 600, fontSize: '1rem' }}
                  >
                    {showAllSectors ? (
                      <>
                        <span>Show Less</span>
                        <ChevronLeft className="w-5 h-5 rotate-90" strokeWidth={2.5} />
                      </>
                    ) : (
                      <>
                        <span>View All {sectors.length} Sectors</span>
                        <ChevronRight className="w-5 h-5 rotate-90" strokeWidth={2.5} />
                      </>
                    )}
                  </motion.button>
                )}
              </div>

              {/* DESKTOP: Horizontal Scroll Container */}
              <div className="hidden md:block relative px-12">
                <div 
                  className="grid grid-rows-2 gap-4 overflow-x-auto pb-4 scrollbar-thin scrollbar-thumb-[#c3a565]/30 scrollbar-track-transparent hover:scrollbar-thumb-[#c3a565]/50"
                  style={{
                    gridAutoFlow: 'column',
                    gridAutoColumns: 'minmax(280px, 340px)',
                    scrollBehavior: 'smooth'
                  }}
                  ref={sectorsScrollRef}
                  onScroll={handleSectorsScroll}
                >
                  {sectors.map((sector, index) => {
                    const Icon = sector.icon;
                    return (
                      <motion.div
                        key={index}
                        initial={{ opacity: 0, x: 30 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.6, delay: index * 0.1 }}
                        className="group"
                      >
                        <div className="bg-white border-2 border-[#c3a565]/30 rounded-xl p-6 hover:border-[#c3a565]/70 hover:shadow-[0_8px_40px_rgba(195,165,101,0.25)] hover:scale-[1.03] hover:bg-[#faf9f7] transition-all duration-500 h-full flex flex-col shadow-[0_2px_15px_rgba(43,43,42,0.08)]">
                          <div className="mb-4 group-hover:scale-110 transition-transform duration-500">
                            <Icon className="w-9 h-9 text-[#c3a565] group-hover:text-[#d4b676] transition-colors duration-500" strokeWidth={1.5} />
                          </div>
                          <span className="text-[#2b2b2a] mb-3 group-hover:text-[#c3a565] transition-colors duration-500" style={{ fontWeight: 600, fontSize: '1.15rem', lineHeight: '1.3' }}>
                            {sector.name}
                          </span>
                          <p className="text-[#2b2b2a]/70 leading-relaxed mb-4 flex-grow group-hover:text-[#2b2b2a]/90 transition-colors duration-500" style={{ fontWeight: 400, fontSize: '0.9rem' }}>
                            {sector.description}
                          </p>
                          {/* <button className="inline-flex items-center gap-2 text-[#c3a565] hover:text-[#d4b676] transition-colors duration-300 group/btn" style={{ fontWeight: 600, fontSize: '0.875rem' }}>
                            <span>Learn More</span>
                            <ArrowRight className="w-4 h-4 group-hover/btn:translate-x-1 transition-transform duration-300" strokeWidth={2.5} />
                          </button> */}
                        </div>
                      </motion.div>
                    );
                  })}
                </div>
                <div className="absolute right-12 top-0 bottom-4 w-16 bg-gradient-to-l from-[#faf9f7] to-transparent pointer-events-none"></div>
                <button
                  onClick={() => handleScrollLeft(sectorsScrollRef)}
                  className={`absolute -left-1 top-1/2 -translate-y-1/2 z-10 w-10 h-10 flex items-center justify-center bg-white border border-[#c3a565]/30 rounded-full hover:bg-[#c3a565]/10 hover:border-[#c3a565]/60 transition-all duration-300 shadow-lg cursor-pointer ${showSectorsLeftArrow ? 'visible' : 'invisible'}`}
                  aria-label="Scroll left"
                >
                  <ChevronLeft className="w-5 h-5 text-[#c3a565]" strokeWidth={2} />
                </button>
                <button
                  onClick={() => handleScrollRight(sectorsScrollRef)}
                  className={`absolute -right-1 top-1/2 -translate-y-1/2 z-10 w-10 h-10 flex items-center justify-center bg-white border border-[#c3a565]/30 rounded-full hover:bg-[#c3a565]/10 hover:border-[#c3a565]/60 transition-all duration-300 shadow-lg cursor-pointer ${showSectorsRightArrow ? 'visible' : 'invisible'}`}
                  aria-label="Scroll right"
                >
                  <ChevronRight className="w-5 h-5 text-[#c3a565]" strokeWidth={2} />
                </button>
              </div>
            </motion.div>
          )}
        </div>
      </div>
    </section>
  );
};